import type { RechtSchluessel } from './rechte/katalog';

/**
 * Welche Projekttypen darf jemand NEU anlegen? (Rollensystem,
 * docs/rollensystem-plan.md §4: `projekte.sigeko_anlegen`, `…sifa_anlegen`,
 * `…hse_anlegen`.)
 *
 * Die Typ-Rechte steuern nur die Anlage — wer ein Projekt bearbeiten darf,
 * regelt weiter die Zuweisung. admin und super_admin haben jedes Recht,
 * Freelancer keins; ohne Rechte aus der Datenbank dürfen alle Internen jeden
 * Typ anlegen wie bisher (`rechteAusSystemrolle`). Die Datenbank (Nr. 154)
 * weist eine Anlage ohne Recht zusätzlich ab (`projects_typ_recht:`, 42501).
 *
 * Reine Funktionen: Aufrufer reichen `hat` aus `useRechte` herein.
 */

/** Projekttyp in `metadata.projectType`; fehlt er, ist es SiGeKo (services/projectType.ts). */
export type AnlageTyp = 'sigeko' | 'sifa' | 'hse';

const ANLEGE_RECHT: Readonly<Record<AnlageTyp, RechtSchluessel>> = {
    sigeko: 'projekte.sigeko_anlegen',
    sifa: 'projekte.sifa_anlegen',
    hse: 'projekte.hse_anlegen',
};

const ANLAGE_TYPEN: readonly AnlageTyp[] = ['sigeko', 'sifa', 'hse'];

/** Das Recht, das die Anlage eines Typs braucht. */
export const anlegeRecht = (typ: AnlageTyp): RechtSchluessel => ANLEGE_RECHT[typ];

/**
 * Projekttyp zu einem Wert aus Assistent, Import oder `metadata.projectType`.
 * Alles außer 'sifa' und 'hse' — auch 'digital', 'other' und ein fehlender
 * Wert — ist SiGeKo. Kleingeschrieben, aber nicht getrimmt verglichen, wie
 * der Trigger der Datenbank (Nr. 154).
 */
export const anlageTypVon = (wert: string | null | undefined): AnlageTyp => {
    const typ = (wert ?? '').toLowerCase();
    return typ === 'sifa' ? 'sifa' : typ === 'hse' ? 'hse' : 'sigeko';
};

/** Darf diesen Projekttyp anlegen? */
export const darfProjektTypAnlegen = (
    hat: (recht: RechtSchluessel) => boolean,
    wert: string | null | undefined,
): boolean => hat(anlegeRecht(anlageTypVon(wert)));

/**
 * Darf ein bestehendes Projekt auf diesen Typ umstellen (Stammdaten)? Der
 * Trigger der Datenbank (Nr. 154) verlangt für einen Typwechsel das
 * Anlegen-Recht des NEUEN Typs. Der aktuelle Typ bleibt immer wählbar.
 */
export const darfProjektTypWechseln = (
    hat: (recht: RechtSchluessel) => boolean,
    aktuell: string | null | undefined,
    neu: string | null | undefined,
): boolean => anlageTypVon(neu) === anlageTypVon(aktuell) || darfProjektTypAnlegen(hat, neu);

/** Darf überhaupt ein Projekt anlegen (Knopf „Neues Projekt")? */
export const darfIrgendeinProjektAnlegen = (hat: (recht: RechtSchluessel) => boolean): boolean =>
    ANLAGE_TYPEN.some(t => hat(anlegeRecht(t)));

/**
 * Die Projekttypen, die jemand anlegen darf — in fester Reihenfolge
 * (SiGeKo, SiFa, HSE). Für die Typwahl bei der Angebotsannahme.
 */
export const erlaubteProjektTypen = (hat: (recht: RechtSchluessel) => boolean): AnlageTyp[] =>
    ANLAGE_TYPEN.filter(t => hat(anlegeRecht(t)));

/**
 * Die Wahl „Art der Dienstleistung" im Anlage-Assistenten. 'digital' und
 * 'other' steuern nur den Nummernkreis und legen ein SiGeKo-Projekt an.
 */
export const ASSISTENT_ARTEN = ['sigeko', 'sifa', 'hse', 'digital', 'other'] as const;
export type AssistentArt = typeof ASSISTENT_ARTEN[number];

/** Die Arten, die der Assistent anbietet — in fester Reihenfolge. */
export const erlaubteAssistentArten = (hat: (recht: RechtSchluessel) => boolean): AssistentArt[] =>
    ASSISTENT_ARTEN.filter(art => darfProjektTypAnlegen(hat, art));

/**
 * Die Art, die gilt: die gewählte, solange sie angeboten wird, sonst die
 * erste angebotene. Darf jemand nur einen Typ anlegen, ist er damit
 * vorgewählt. `null` = nichts erlaubt.
 */
export const gueltigeAssistentArt = (
    gewaehlt: string,
    erlaubt: readonly AssistentArt[],
): AssistentArt | null =>
    erlaubt.find(art => art === gewaehlt) ?? erlaubt[0] ?? null;
